<template>
    <div class="mt-3">
        <div class="flex flex-col gap-3 md:grid md:grid-cols-3 md:items-center">
            <div class="flex justify-center md:justify-start">
                <h3 v-if="selected.length" class="text-lg text-theme-base">
                    Inventario seleccionado {{ selected.length }}
                </h3>
            </div>
            <h1 class="text-3xl capitalize text-center">
                Inventario
                {{ selectedMerchant.merchantType | merchantInfo("type") }}
                <strong class="text-theme-primary">
                    {{ selectedMerchant.name }}
                </strong>
                {{ itemsNumber }}
            </h1>
            <div class="flex justify-center md:justify-end">
                <app-button
                    v-if="$ability.hasPermission('inventory:Create')"
                    color="success"
                    size="large"
                    @click="$router.push('/app/inventories/new')"
                >
                    <span class="hidden md:inline">
                        Nuevo registro de inventario
                    </span>
                    <span class="md:hidden font-bold">+</span>
                </app-button>
            </div>
        </div>

        <div class="flex flex-wrap">
            <div class="mt-3 w-full">
                <products-filters
                    v-model="query"
                    :enabled-filters="$data.$enabledFilters"
                    @enter="reloadItems"
                />
                <div class="flex justify-center">
                    <app-button relief @click="reloadItems">
                        Consultar 🔎
                    </app-button>
                </div>
            </div>

            <div
                class="flex flex-col lg:flex-row items-center justify-between w-full mt-3 gap-4"
            >
                <div
                    class="flex flex-wrap items-center justify-center lg:justify-start gap-2"
                >
                    <advanced-options-button
                        v-if="
                            $ability.hasPermission('inventory:List') ||
                            $ability.hasPermission('inventory:SyncPrices')
                        "
                        :options="advancedOptions"
                        @option-click="onAdvancedOptionClick"
                    />
                    <tooltip-button
                        v-show="selected.length"
                        icon="👀"
                        message="Vista previa de referencias seleccionadas"
                        @click="openPreview"
                    />
                </div>

                <div
                    class="flex flex-wrap items-center justify-center lg:justify-end gap-3"
                >
                    <list-settings-button
                        :value="{ pageSize }"
                        @change="onListSettingsChange"
                    />
                    <custom-pagination
                        v-model="currentPage"
                        :length="pagesNumber"
                        @input="listItems()"
                    />
                </div>
            </div>
        </div>

        <div class="w-full mt-2">
            <custom-sortable-table
                ref="selectableTable"
                :fields="$data.$fields"
                :items="itemsCurrent"
                :per-page="itemsCurrent.length"
                select-mode="multi"
                responsive
                selectable
                outlined
                class="w-full"
                items-empty-message="Nuestro sistema NO encuentra resultados. Intenta modificar los filtros de búsqueda."
                :row-actions="rowActions"
                @flex-selected="onRowSelected"
                @row-action="onRowAction"
            >
                <template #cell(image)="data">
                    <router-link
                        :to="`/app/inventories/${selectedMerchant.merchantType}/${selectedMerchant.merchantId}/detail/${data.item.id}`"
                    >
                        <div class="w-12 h-12 flex items-center justify-center">
                            <img
                                v-if="
                                    data.item.images && data.item.images.length
                                "
                                v-img-loading
                                :src="data.item.images[0].url | image"
                                :alt="data.item.images[0].alt"
                                class="w-12 h-12 object-cover rounded"
                                loading="lazy"
                            />
                            <span v-else class="text-2xl">📦</span>
                        </div>
                    </router-link>
                </template>

                <template #cell(name)="data">
                    <router-link
                        :to="`/app/inventories/${selectedMerchant.merchantType}/${selectedMerchant.merchantId}/detail/${data.item.id}`"
                        class="btn-a"
                    >
                        <small class="mr-1">{{
                            data.item.active === undefined
                                ? ""
                                : data.item.active
                                ? "🟢"
                                : "⚪"
                        }}</small>
                        {{ data.item.product.name | cut(40) }}
                    </router-link>
                    <div>
                        <small class="text-theme-muted">
                            {{
                                data.item.product.sku
                                    ? `SKU ${data.item.product.sku}`
                                    : `ID ${data.item.productId}`
                            }}
                        </small>
                    </div>
                </template>

                <template #cell(barcode)="data">
                    <small>{{ data.item.barcode }}</small>
                </template>

                <template #cell(variant)="data">
                    <div class="flex flex-col gap-0.5">
                        <small
                            v-if="data.item.color"
                            class="capitalize flex items-center gap-1"
                        >
                            <span
                                class="inline-block w-3 h-3 rounded-full border border-theme-border flex-shrink-0"
                                :style="{
                                    backgroundColor: data.item.colorRef
                                }"
                            ></span>
                            {{ data.item.color }}
                        </small>
                        <small v-if="data.item.size" class="capitalize">
                            📐 {{ data.item.size }}
                        </small>
                        <small
                            v-if="data.item.customReferenceValue"
                            class="capitalize"
                        >
                            {{ data.item.customReferenceValue }}
                        </small>
                    </div>
                </template>

                <template #cell(stockMerchant)="data">
                    <span
                        class="font-bold"
                        :class="{
                            'text-theme-success': data.item.stockMerchant > 0,
                            'text-theme-warning': data.item.stockMerchant === 0
                        }"
                    >
                        {{ data.item.stockMerchant }}
                    </span>
                </template>

                <template #cell(priceOffer)="data">
                    <span class="font-bold">{{
                        data.item.priceOffer | money
                    }}</span>
                    <div v-if="data.item.priceOffer < data.item.price">
                        <small class="line-through text-theme-muted">{{
                            data.item.price | money
                        }}</small>
                    </div>
                </template>
            </custom-sortable-table>
        </div>

        <app-dialog v-model="isOpenPreview" size="lg">
            <template #header>
                <div
                    class="mx-4 mt-2 flex justify-center items-center flex-col"
                >
                    <h3 class="text-xl text-theme-primary font-bold">
                        Vista Previa de Variantes
                    </h3>
                    <p class="text-sm mt-1 text-theme-muted">
                        {{ selected.length }} referencia{{
                            selected.length !== 1 ? "s" : ""
                        }}
                        seleccionada{{ selected.length !== 1 ? "s" : "" }}
                    </p>
                </div>
            </template>

            <div class="px-4">
                <div class="max-h-600px overflow-y-auto">
                    <div
                        v-for="item in selected"
                        :key="item.id"
                        class="mb-4 border rounded-lg p-4 shadow-sm flex gap-4 items-start"
                    >
                        <div class="flex-shrink-0 flex flex-col gap-1">
                            <img
                                v-if="item.images && item.images.length"
                                v-img-loading
                                :src="item.images[0].url | image"
                                :alt="item.images[0].alt"
                                class="w-24 h-24 object-cover rounded-lg shadow"
                                loading="lazy"
                            />
                            <div
                                v-else
                                class="w-24 h-24 rounded-lg flex items-center justify-center text-4xl"
                            >
                                📦
                            </div>
                            <div
                                v-if="item.images && item.images.length > 1"
                                class="flex gap-1 flex-wrap"
                            >
                                <img
                                    v-for="img in item.images.slice(1, 5)"
                                    :key="img.id"
                                    v-img-loading
                                    :src="img.url | image"
                                    :alt="img.alt"
                                    class="w-8 h-8 object-cover rounded border"
                                    loading="lazy"
                                />
                            </div>
                        </div>

                        <div class="flex-1 min-w-0">
                            <div class="flex items-start gap-2 mb-2">
                                <span>{{ item.active ? "🟢" : "⚪" }}</span>
                                <router-link
                                    :to="`/app/inventories/${selectedMerchant.merchantType}/${selectedMerchant.merchantId}/detail/${item.id}`"
                                    class="text-base font-bold text-theme-primary leading-tight"
                                    @click.native="isOpenPreview = false"
                                >
                                    {{ item.product.name }}
                                </router-link>
                            </div>

                            <div
                                class="grid grid-cols-2 gap-x-4 gap-y-1 text-sm mb-2"
                            >
                                <div>
                                    <span class="font-semibold">Código: </span>
                                    {{ item.barcode || item.id }}
                                </div>
                                <div>
                                    <span class="font-semibold">SKU: </span>
                                    {{ item.product.sku || item.productId }}
                                </div>
                                <div v-if="item.color">
                                    <span class="font-semibold">Color: </span>
                                    <span class="capitalize">{{
                                        item.color
                                    }}</span>
                                    <span
                                        class="inline-block w-3 h-3 rounded-full border border-theme-border ml-1 align-middle"
                                        :style="{
                                            backgroundColor: item.colorRef
                                        }"
                                    ></span>
                                </div>
                                <div v-if="item.size">
                                    <span class="font-semibold">Talla: </span>
                                    <span class="capitalize">{{
                                        item.size
                                    }}</span>
                                </div>
                                <div v-if="item.customReferenceValue">
                                    <span class="font-semibold">
                                        {{
                                            item.product.customReferenceTitle ||
                                            "Variante"
                                        }}:
                                    </span>
                                    <span class="capitalize">{{
                                        item.customReferenceValue
                                    }}</span>
                                </div>
                                <div>
                                    <span class="font-semibold">Stock: </span>
                                    <span
                                        class="font-bold"
                                        :class="{
                                            'text-theme-success':
                                                item.stockMerchant > 0,
                                            'text-theme-warning':
                                                item.stockMerchant === 0
                                        }"
                                    >
                                        {{ item.stockMerchant }}
                                    </span>
                                </div>
                            </div>

                            <div class="flex items-center gap-3">
                                <span
                                    class="text-xl font-bold text-theme-primary"
                                >
                                    {{ item.priceOffer | money }}
                                </span>
                                <span
                                    v-if="item.priceOffer < item.price"
                                    class="text-sm line-through text-theme-muted"
                                >
                                    {{ item.price | money }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </app-dialog>
    </div>
</template>

<script>
import urlQuerySyncMixin from "@/mixins/urlQuerySyncMixin";
import { mapActions, mapGetters, mapState } from "vuex";

import { downloadFileMassiveInventory } from "@/common/lib/excel";

import ProductsFilters from "@/components/products/ProductsFilters.vue";
import AdvancedOptionsButton from "@/components/utils/AdvancedOptionsButton.vue";
import AppDialog from "@/components/utils/AppDialog.vue";
import CustomSortableTable from "@/components/utils/CustomSortableTable.vue";
import TooltipButton from "@/components/utils/TooltipButton.vue";

export default {
    name: "InventoryIndex",
    components: {
        AppDialog,
        AdvancedOptionsButton,
        CustomSortableTable,
        ProductsFilters,
        TooltipButton
    },
    mixins: [urlQuerySyncMixin],
    data: () => ({
        isLoadingDownload: false,
        isLoading: false,
        itemsCurrent: [],
        selected: [],
        isOpenPreview: false,
        $enabledFilters: ["brand", "line", "keyword", "barcode", "sku"],
        $urlPaginationKey: "page",
        $urlFilterSchema: [
            { key: "keyword", type: "string" },
            { key: "brandSlug", type: "string" },
            { key: "lineSlug", type: "string" },
            { key: "sku", type: "string" },
            { key: "barcode", type: "string" }
        ],
        $fields: [
            { key: "image", label: "Foto", sortable: false },
            { key: "name", label: "Nombre / SKU", sortable: false },
            { key: "barcode", label: "Código", sortable: false },
            { key: "variant", label: "Variante", sortable: false },
            { key: "stockMerchant", label: "Stock", sortable: true },
            { key: "priceOffer", label: "Precio de venta", sortable: true }
        ],
        currentPage: 1,
        pagesNumber: 0,
        itemsNumber: 0,
        pageSize: 24,
        query: {}
    }),
    computed: {
        ...mapGetters("control", ["backgroundColor"]),
        ...mapState("commons", ["selectedMerchant"]),
        ...mapState("inventories", ["references"]),
        rowActions() {
            if (this.selected.length === 1) {
                return [{ key: "preview", icon: "👀", label: "Vista previa" }];
            }

            return [];
        },
        advancedOptions() {
            const opts = [];
            if (this.$ability.hasPermission("inventory:List")) {
                opts.push({
                    key: "download",
                    icon: "⬇",
                    label: "Descargar todo el inventario",
                    group: "action"
                });
            }
            if (this.$ability.hasPermission("inventory:SyncPrices")) {
                opts.push({
                    key: "sync",
                    icon: "🔁",
                    label: `Sincronizar precios con ${this.$siteType}`,
                    group: "action"
                });
            }
            return opts;
        }
    },
    watch: {
        selectedMerchant() {
            this.currentPage = 1;
            this.listItems(true);
        }
    },
    async mounted() {
        const verifyPagePermission =
            this.$ability.verifyPagePermission.bind(this);
        if (!(await verifyPagePermission("inventory:List", "/app", true)))
            return;
        this.listItems(true);
    },
    methods: {
        ...mapActions("inventories", [
            "listMassiveInventories",
            "listReferencesInInventory",
            "syncPricesInventory"
        ]),
        async listItems(complete = false) {
            this.isLoading = true;
            this.$loading.show();
            try {
                const { items, pagination } =
                    await this.listReferencesInInventory({
                        ...this.query,
                        complete,
                        itemsByPage: this.pageSize,
                        merchantId: this.selectedMerchant.merchantId,
                        merchantType: this.selectedMerchant.merchantType,
                        page: this.currentPage
                    });
                this.itemsCurrent = items;
                if (pagination) {
                    this.itemsNumber = pagination.itemsNumber;
                    this.pagesNumber = pagination.pagesNumber;
                }
                this.isLoading = false;
                this.$loading.hide();
            } catch (error) {
                this.isLoading = false;
                this.$loading.hide();
                this.isLoading = false;
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async reloadItems() {
            this.currentPage = 1;
            await this.listItems(true);
        },
        onListSettingsChange({ pageSize }) {
            this.pageSize = pageSize;
            this.reloadItems();
        },
        onAdvancedOptionClick(key) {
            if (key === "download") this.downLoadInventory();
            if (key === "sync") this.syncPricesInventoryComponent();
        },
        onRowSelected(items) {
            this.selected = items;
        },
        openPreview() {
            this.isOpenPreview = true;
        },
        clearSelected() {
            this.selected = [];
            if (this.$refs.selectableTable) {
                this.$refs.selectableTable.selectedIndex = [];
                this.$refs.selectableTable.selectedRows = [];
            }
        },
        onRowAction({ action }) {
            switch (action) {
                case "preview":
                    this.openPreview();
                    break;
            }
        },
        async syncPricesInventoryComponent() {
            let reload = false;
            const dialogResult = await this.$swal.fire({
                background: this.backgroundColor,
                title: "Vas a sincronizar los precios de tu inventario ?",
                text: `Quieres que TODOS los precios del tienda${this.selectedMerchant.name} tendrá el mismo precio de la referencia en el ${this.$siteType}. Para reestablecer un precio debes entrar el detalle de la referencia.`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#02913f",
                cancelButtonColor: "#3085d6",
                confirmButtonText: "Si",
                cancelButtonText: "No"
            });
            if (!dialogResult.isConfirmed) {
                return;
            }

            this.isLoading = true;
            this.$loading.show();
            try {
                await this.syncPricesInventory({
                    merchantType: this.selectedMerchant.merchantType,
                    merchantId: this.selectedMerchant.merchantId
                });
                reload = true;
                this.$sound.play();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Actualización exitosa",
                    icon: "success",
                    showCloseButton: true,
                    timer: 3000
                });
                this.$loading.hide();
                this.isLoading = false;
            } catch (error) {
                this.$loading.hide();
                this.isLoading = false;
                this.$loading.hide();
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }

            if (reload) {
                this.reloadItems();
            }
        },
        async downLoadInventory() {
            this.isLoadingDownload = true;
            try {
                const { items } = await this.listMassiveInventories(
                    this.selectedMerchant
                );
                downloadFileMassiveInventory(
                    `inventory_${this.selectedMerchant.name}`,
                    items
                );
                this.isLoadingDownload = false;
            } catch (error) {
                this.isLoadingDownload = false;
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        }
    }
};
</script>
